import { PropsWithChildren, useState, FunctionComponent } from 'react'
import { useMutation } from '@apollo/client'
import { Alert, message, Spin } from 'antd'
import { sendEmailVerification as SendEmailVerificationType } from '@User/MyAccountPage/AccountSettings/__generated__/sendEmailVerification'
import { sendEmailVerificationMutation } from '@User/MyAccountPage/AccountSettings/mutations'
import { SUCCESSFUL_LOGIN } from '@User/LoginPage/constants'

import Box from '~/core/Box'
import Link from '~/core/Link'
import LocalStorageWrapper from '~/lib/LocalStorageWrapper'
import logger from '~/lib/logger'
import { EmailVerificationStatus } from '~/__generated__/globalTypes'
import {
    BusinessAccountAlertDocument,
    BusinessAccountSignupStatus,
    EmailVerificationStatusDocument,
    useEmailVerificationStatusQuery,
} from '~/gql/graphql'

import styles from './styles.css'

const USER_DISMISSED_VERIFY_EMAIL_ALERT = 'userDismissedVerifyEmailAlert'

type VerifyEmailAddressAlertPropsType = {
    placement: string
}

// Alert for non Business Account users
const VerifyEmailAddressAlert: FunctionComponent<
    PropsWithChildren<VerifyEmailAddressAlertPropsType>
> = (props: VerifyEmailAddressAlertPropsType) => {
    const { placement } = props

    const { data: emailVerificationData } = useEmailVerificationStatusQuery()
    const [sendingEmailVerification, setSendingEmailVerification] =
        useState<boolean>(false)
    const [emailVerificationSent, setEmailVerificationSent] =
        useState<boolean>(false)
    const [sendEmailVerification] = useMutation<SendEmailVerificationType>(
        sendEmailVerificationMutation,
        {
            refetchQueries: [
                { query: BusinessAccountAlertDocument },
                { query: EmailVerificationStatusDocument },
            ],
        },
    )

    const storage = new LocalStorageWrapper()

    const userHasLoggedIn = storage.getItem(SUCCESSFUL_LOGIN)
    const userHasDismissed = storage.getItem(USER_DISMISSED_VERIFY_EMAIL_ALERT)

    if (userHasLoggedIn && userHasDismissed) {
        storage.removeItem(USER_DISMISSED_VERIFY_EMAIL_ALERT)
        storage.removeItem(SUCCESSFUL_LOGIN)
    }

    const showAlert =
        emailVerificationData?.me &&
        emailVerificationData?.me?.businessAccountSignupStatus !==
            BusinessAccountSignupStatus.pending_email_verification &&
        emailVerificationData?.me.emailVerificationStatus ==
            EmailVerificationStatus.pending &&
        !userHasDismissed

    if (!showAlert || emailVerificationSent) {
        return null
    }

    const onClose = () => {
        storage.setItem(USER_DISMISSED_VERIFY_EMAIL_ALERT, true)
    }

    const onResendEmail = async (evt: Event) => {
        evt.preventDefault()

        setSendingEmailVerification(true)
        const variables = { userId: emailVerificationData?.me?.id }

        try {
            await sendEmailVerification({ variables })
            message.success('Email sent successfully!')
            setEmailVerificationSent(true)
        } catch (err) {
            logger.exception(err, {
                extra: {
                    info: `${placement}:: Failed to send verification email: `,
                    variables,
                },
            })
            message.error(
                'We encountered an error while trying to send the email',
            )
        } finally {
            setSendingEmailVerification(false)
        }
    }

    const AlertMessage = () => {
        const messageBody = (
            <>
                For added security, an email was sent to{' '}
                {emailVerificationData?.me?.email}. Please check your email and
                click <strong>verify email address</strong> in the message.{' '}
                <Link
                    name="resend-email-verification-link"
                    placement={placement}
                    onClick={onResendEmail}
                >
                    Resend email now
                </Link>
            </>
        )

        return (
            <Alert
                message={messageBody}
                onClose={onClose}
                type="warning"
                closable
                style={{ width: '100%' }}
            />
        )
    }

    if (sendingEmailVerification) {
        return (
            <Box marginBottom="12px">
                <Spin wrapperClassName={styles.verifyEmailAddressAlertSpinner}>
                    <AlertMessage />
                </Spin>
            </Box>
        )
    }

    return (
        <Box marginBottom="12px">
            <AlertMessage />
        </Box>
    )
}

export default VerifyEmailAddressAlert
